import { RequirementsList } from "@certn/cascadia-react";
import { getScopeLookupPath } from "@certn/forms/src/controls/utils";
import { RequirementDisplayStyle } from "@certn/forms/src/controls/validationRulesUtils";
import get from "lodash/get";
import { useMemo } from "react";

export function resolveRequirementDisplayStyles(
  requirementMessages: string[],
  requirementSets: string[][],
  data: Array<{ document_type?: string }> | undefined,
  errors: string[],
  isSubmitted: boolean | undefined,
  label: string
): Array<{ text: string; style: RequirementDisplayStyle }> {
  const requirementMet =
    requirementSets.length === requirementMessages.length
      ? requirementSets.map((set) =>
          (data ?? []).some(
            (item) =>
              item?.document_type != null && set.includes(item.document_type)
          )
        )
      : undefined;

  // Deduplicate by message text — the backend may combine citizen and
  // non-citizen validation messages, producing duplicates for
  // single-citizenship countries (e.g. Italy).
  const seen = new Set<string>();
  return requirementMessages.reduce<
    Array<{ text: string; style: RequirementDisplayStyle }>
  >((acc, requirement, index) => {
    const text = label ? `${label} ${requirement}` : requirement;
    if (seen.has(text)) return acc;
    seen.add(text);

    let style: RequirementDisplayStyle = "normal";
    if (requirementMet !== undefined) {
      style = requirementMet[index]
        ? "success"
        : isSubmitted
          ? "error"
          : "normal";
    } else {
      const found = errors.some((error) => error.includes(requirement));
      style = !found ? "success" : isSubmitted ? "error" : "normal";
    }
    acc.push({ text, style });
    return acc;
  }, []);
}

export interface IdentityDocumentFilesRequirementsListProps {
  rootSchema: Record<string, unknown>;
  uischemaScope: string;
  data: Array<{ document_type?: string }> | undefined;
  errors: string[];
  isSubmitted: boolean | undefined;
  label: string;
}

export function IdentityDocumentFilesRequirementsList({
  rootSchema,
  uischemaScope,
  data,
  errors,
  isSubmitted,
  label,
}: IdentityDocumentFilesRequirementsListProps) {
  const requirementsList = useMemo(() => {
    const scopePath = getScopeLookupPath(uischemaScope);
    const fieldSchema = get(rootSchema, scopePath) as
      | {
          requirements?: string[];
          requirementSets?: string[][];
        }
      | undefined;
    const requirementMessages = fieldSchema?.requirements;
    const requirementSets = fieldSchema?.requirementSets ?? [];

    if (!requirementMessages?.length) return null;

    return resolveRequirementDisplayStyles(
      requirementMessages,
      requirementSets,
      data,
      errors,
      isSubmitted,
      label
    );
  }, [rootSchema, uischemaScope, data, errors, isSubmitted, label]);

  if (!requirementsList?.length) return null;

  return <RequirementsList requirementsList={requirementsList} />;
}
